/* eslint-disable no-self-assign */ // Copyright 2018 Google LLC
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     https://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
/**
 * @module elements-sk/checkbox-sk
 * @description <h2><code>checkbox-sk</code></h2>
 *
 * <p>
 *   The checkbox-sk and element contains a native 'input'
 *   element in light DOM so that it can participate in a form element.
 * </p>
 *
 * <p>
 *    Each element also supports the following attributes exactly as the
 *    native checkbox element:
 *    <ul>
 *      <li>checked</li>
 *      <li>disabled</li>
 *      <li>name</li>
 *     </ul>
 * </p>
 *
 * <p>
 *    All the normal events of a native checkbox are supported.
 * </p>
 *
 * @attr label - A string, with no markup, that is to be used as the label for
 *            the checkbox. If you wish to have a label with markup then set
 *            'label' to the empty string and create your own
 *            <code>label</code> element in the DOM with the 'for' attribute
 *            set to match the name of the checkbox-sk.
 *
 * @prop checked This mirrors the checked attribute.
 * @prop disabled This mirrors the disabled attribute.
 * @prop name This mirrors the name attribute.
 * @prop label This mirrors the label attribute.
 *
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "CheckOrRadio", {
    enumerable: true,
    get: function() {
        return CheckOrRadio;
    }
});
const _lit = require("lit");
const _decorators = require("lit/decorators.js");
const _define = require("../define");
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let CheckOrRadio = class CheckOrRadio extends _lit.LitElement {
    get _role() {
        return 'checkbox';
    }
    // Allow subclasses to override icons
    get checkedIcon() {
        return 'check_box';
    }
    get uncheckedIcon() {
        return 'check_box_outline_blank';
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this.handleHostClick);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this.handleHostClick);
    }
    createRenderRoot() {
        return this;
    }
    render() {
        return (0, _lit.html)`
      <label for="${this._role}-${this.uniqueId}">
        <input
          type=${this._role}
          name=${this.name}
          id="${this._role}-${this.uniqueId}"
          ?disabled=${this.disabled}
          .checked=${this.checked}
          @change=${this.handleChange} />
        <span class="icons">
          <span class="icon-sk unchecked">${this.uncheckedIcon}</span>
          <span class="icon-sk checked">${this.checkedIcon}</span>
        </span>
        <span class="label">${this.label}</span>
      </label>
    `;
    }
    handleChange(e) {
        // The native input event 'change' bubbles, but we want to ensure
        // the property is updated.
        this.checked = e.target.checked;
    }
    constructor(...args){
        super(...args);
        this.uniqueId = `${CheckOrRadio.nextUniqueId++}`;
        this.checked = false;
        this.disabled = false;
        this.name = '';
        this.label = '';
        this.handleHostClick = (e)=>{
            if (e.target === this) {
                var _input;
                if (this.checked && this._role === 'radio') {
                    return;
                }
                const input = this.querySelector('input');
                (_input = input) === null || _input === void 0 ? void 0 : _input.click();
            }
        };
    }
};
(()=>{
    CheckOrRadio.nextUniqueId = 0;
})();
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true
    }),
    _ts_metadata("design:type", Boolean)
], CheckOrRadio.prototype, "checked", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: Boolean,
        reflect: true
    }),
    _ts_metadata("design:type", Boolean)
], CheckOrRadio.prototype, "disabled", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String,
        reflect: true
    }),
    _ts_metadata("design:type", String)
], CheckOrRadio.prototype, "name", void 0);
_ts_decorate([
    (0, _decorators.property)({
        type: String,
        reflect: true
    }),
    _ts_metadata("design:type", String)
], CheckOrRadio.prototype, "label", void 0);
(0, _define.define)('checkbox-sk', CheckOrRadio);
